// src/pages/ArchiveTransfersPage.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

export default function ArchiveTransfersPage() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [detailItems, setDetailItems] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const pharmacyCode = String(user?.pharmacyCode || "");

  const formatExpiration = (value) => {
    if (!value) return "-";
    const clean = String(value).replace(/\s/g, "");
    const d = new Date(clean);
    if (!isNaN(d)) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = d.getFullYear();
      return `${dd}.${mm}.${yy}`;
    }
    const m = clean.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return clean;
  };

  // 🔹 načtení archivních převodek
  useEffect(() => {
    if (!pharmacyCode) {
      setError("Nepodařilo se načíst údaje o pobočce.");
      setLoading(false);
      return;
    }

    async function fetchTransfers() {
      try {
        const res = await fetch(
          `/api/transfers/archive/LEMON/${pharmacyCode}`,
          { method: "GET", credentials: "include" }
        );

        if (!res.ok) throw new Error("Chyba načtení");

        const json = await res.json();
        setData(Array.isArray(json) ? json : []);
      } catch (err) {
        console.error(err);
        setError("Nepodařilo se načíst archivní převodky.");
      } finally {
        setLoading(false);
      }
    }

    fetchTransfers();
  }, [pharmacyCode]);

  const loadDetail = async (prevodkaId) => {
    try {
      const res = await fetch(`/api/transfers/detail/${prevodkaId}`, {
        method: "GET",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Chyba detailu");

      const json = await res.json();
      setDetailItems(json || []);
      setSelectedTransfer(prevodkaId);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      alert("Nepodařilo se načíst položky převodky.");
    }
  };

  if (loading) return <div className="p-4 text-gray-600">Načítám data...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-6">
        Archiv převodek – pobočka {pharmacyCode}
      </h1>

      {data.length > 0 ? (
        <table className="min-w-full border-collapse bg-white shadow rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4 text-left">Číslo převodky</th>
              <th className="py-3 px-4 text-left">Zdrojová pobočka</th>
              <th className="py-3 px-4 text-left">Cílová pobočka</th>
              <th className="py-3 px-4 text-left">Stav</th>
              <th className="py-3 px-4 text-left">Datum přijetí</th>
              <th className="py-3 px-4 text-left">Akce</th>
            </tr>
          </thead>

          <tbody>
            {data.map((t) => (
              <tr key={t._id} className="border-t hover:bg-gray-50">
                <td className="py-3 px-4">{t.prevodkaId}</td>
                <td className="py-3 px-4">{t.fromPharmacyCode}</td>
                <td className="py-3 px-4">{t.toPharmacyCode}</td>
                <td className="py-3 px-4 capitalize">{t.stav}</td>
                <td className="py-3 px-4">
                  {t.receivedAt
                    ? new Date(t.receivedAt).toLocaleDateString("cs-CZ", {
                        timeZone: "Europe/Prague",
                      })
                    : ""}
                </td>

                <td className="py-3 px-4">
                  <button
                    onClick={() => loadDetail(t.prevodkaId)}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12s-3.75 6.75-9.75 6.75S2.25 12 2.25 12z"
                      />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>Detail</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-gray-500">Žádné archivní převodky.</div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">
                Položky převodky {selectedTransfer}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto">
              <table className="min-w-full border-collapse bg-white">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="py-2 px-3 text-left">PDK</th>
                    <th className="py-2 px-3 text-left">Název</th>
                    <th className="py-2 px-3 text-left">Množství</th>
                    <th className="py-2 px-3 text-left">NCSD</th>
                    <th className="py-2 px-3 text-left w-40">Exspirace</th>
                    <th className="py-2 px-3 text-left">Šarže</th>
                    <th className="py-2 px-3 text-left">Dodavatel</th>
                  </tr>
                </thead>

                <tbody>
                  {detailItems.map((i) => (
                    <tr key={i._id} className="border-t hover:bg-gray-50">
                      <td className="py-2 px-3">{i.PDK}</td>
                      <td className="py-2 px-3">{i.Název}</td>
                      <td className="py-2 px-3">{i.Množství}</td>
                      <td className="py-2 px-3">{i.NCSD ? i.NCSD.toFixed(2) : "-"}</td>
                      <td className="py-2 px-3 w-40">
                        {formatExpiration(i.Exspirace)}
                      </td>
                      <td className="py-2 px-3">{i.Šarže}</td>
                      <td className="py-2 px-3">{i.Dodavatel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
